'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Input, SettingRow, toast } from '@kiao-voice/ui-kit'
import { billingStatClassName } from '@/components/billing-stat'
import { readStoredToken } from '@/features/auth/auth-storage'
import { useActiveContext } from '@/lib/use-active-context'
import { fetchWallet, type WalletResponse } from './api-client'

interface Props {
  getTokenFn?: () => string | null
  /** Injected for tests. */
  fetchWalletFn?: typeof fetchWallet
  /** Open the unified checkout modal to top up `amount` (NT$). The actual charge
   *  runs through the shared CheckoutDialog (wallet-topup strategy) on the
   *  billing page — this panel only displays the balance + captures the amount. */
  onTopUp: (amount: number) => void
  /** Bump to force a balance re-fetch (e.g. after a top-up completes). */
  reloadSignal?: number
  /** Team-seat member: read-only — the owner manages account credit. */
  seated?: boolean
}

/**
 * 抵免額 (account credit) row in the 02 section of /account billing
 * (numbered-section design, app-account.html). Self-gating: renders nothing
 * until the server reports `enabled:true`. Shows the NT$ balance + an amount
 * input whose 前往加值 button opens the shared checkout modal. The term is
 * 抵免額／加值 throughout (never 儲值 — Taiwan stored-value regulation).
 */
export function WalletPanel({
  getTokenFn = readStoredToken,
  fetchWalletFn = fetchWallet,
  onTopUp,
  reloadSignal,
  seated = false,
}: Props) {
  const { t } = useTranslation('web')
  // The credit pool is resolved server-side from the active context; switching
  // workspaces doesn't remount this panel, so re-fetch on context change.
  const activeContext = useActiveContext()
  const [wallet, setWallet] = useState<WalletResponse | null>(null)
  const [amount, setAmount] = useState<number | null>(null)

  const refresh = useCallback(async () => {
    const token = getTokenFn()
    if (!token) return
    try {
      const data = await fetchWalletFn()
      setWallet(data)
      setAmount((current) => current ?? data.minTopUpTwd)
    } catch (cause) {
      reportHandledException(cause, { source: 'features/billing/wallet-panel.tsx' })
      // Non-critical, flag-gated panel — hide it on failure, but REPORT (no
      // silent catch) so a systematic outage isn't invisible.
      console.warn('wallet: failed to load account-credit panel — hiding it', cause)
      setWallet(null)
    }
  }, [getTokenFn, fetchWalletFn])

  useEffect(() => {
    void refresh()
  }, [refresh, activeContext, reloadSignal])

  // Self-gate: hidden until the server enables the feature.
  if (!wallet || !wallet.enabled) return null

  // Team-seat member: read-only — the team owner manages credit.
  if (seated) {
    return (
      <p className="note" data-testid="wallet-member-note" style={{ margin: 0 }}>
        {t('billing.walletMemberNote')}
      </p>
    )
  }

  // Clamp the amount into [min, max] on blur (the design's 100–100000 range).
  const clampAmount = () => {
    if (amount === null) return
    const next = Math.min(wallet.maxTopUpTwd, Math.max(wallet.minTopUpTwd, amount))
    if (next !== amount) setAmount(next)
  }

  const handleTopUp = () => {
    if (amount === null) return
    if (amount < wallet.minTopUpTwd || amount > wallet.maxTopUpTwd) {
      toast.error(
        t('billing.walletAmountRange', { min: wallet.minTopUpTwd, max: wallet.maxTopUpTwd })
      )
      return
    }
    onTopUp(amount)
  }

  return (
    <SettingRow
      data-testid="wallet-panel"
      label={
        <div className={billingStatClassName} data-testid="wallet-balance">
          NT$ {wallet.total.toLocaleString('en-US')}
        </div>
      }
      control={
        <span className="flex flex-wrap items-center justify-end gap-3.5 text-right">
          <label htmlFor="wallet-topup-amount" className="text-xs text-muted">
            {t('billing.walletAmountLabelShort')}
          </label>
          <Input
            id="wallet-topup-amount"
            data-testid="wallet-topup-amount"
            className="w-24 font-mono tabular-nums"
            aria-label={t('billing.walletAmountLabel', {
              min: wallet.minTopUpTwd,
              max: wallet.maxTopUpTwd,
            })}
            type="number"
            inputMode="numeric"
            min={wallet.minTopUpTwd}
            max={wallet.maxTopUpTwd}
            step={1}
            // Fixed width (not width:100%+maxWidth) so the input contributes its
            // real size to .bctl's intrinsic width — otherwise a percentage width
            // makes flexbox under-size .bctl and squish the label/button.
            style={{ width: 96 }}
            value={amount ?? ''}
            onChange={(e) => {
              const next = Math.floor(Number(e.target.value))
              // Guard non-numeric input → null (treated as "no amount"), so a NaN
              // can never reach onTopUp / the charge payload.
              setAmount(e.target.value === '' || !Number.isFinite(next) ? null : next)
            }}
            onBlur={clampAmount}
          />
          <Button
            variant="primary"
            size="control"
            data-testid="wallet-topup-open"
            onClick={handleTopUp}
          >
            {t('billing.walletTopUpCta')}
          </Button>
        </span>
      }
    />
  )
}
